import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'
import { parseGallery, type GalleryItem } from '@/lib/gallery'
import { VideoEmbed } from './VideoEmbed'

interface MediaGalleryProps {
  /** Stored gallery items: "media:<id>" or video links. */
  items: readonly string[]
  /** What the gallery is of, for labels: "Blood Banner Alpha". */
  title: string
  className?: string
}

function Thumb({ item }: { item: GalleryItem }) {
  if (item.kind === 'image') return <img src={item.thumb} alt="" loading="lazy" />
  return (
    <>
      {item.video.thumbnail ? (
        <img src={item.video.thumbnail} alt="" loading="lazy" />
      ) : (
        <span className="media-gallery-thumb-provider">Vimeo</span>
      )}
      <span className="media-gallery-thumb-play" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M8 5.5v13l11-6.5z" />
        </svg>
      </span>
    </>
  )
}

/** A row of thumbnails; each opens a full-screen viewer at that item. */
export function MediaGallery({ items, title, className }: MediaGalleryProps) {
  const parsed = useMemo(() => parseGallery(items), [items])
  const [open, setOpen] = useState<number | null>(null)

  if (parsed.length === 0) return null

  return (
    <section className={cn('media-gallery', className)} aria-label={`${title} gallery`}>
      <div className="media-gallery-strip">
        {parsed.map((item, index) => (
          <button
            type="button"
            key={item.key}
            className={cn('media-gallery-thumb', item.kind === 'video' && 'is-video')}
            onClick={(event) => {
              event.stopPropagation()
              setOpen(index)
            }}
            aria-label={`${item.kind === 'video' ? 'Play video' : 'View image'} ${index + 1} of ${parsed.length}`}
          >
            <Thumb item={item} />
          </button>
        ))}
      </div>
      {open !== null ? (
        <GalleryViewer
          items={parsed}
          index={open}
          title={title}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  )
}

interface GalleryViewerProps {
  items: GalleryItem[]
  index: number
  title: string
  onIndex: (index: number) => void
  onClose: () => void
}

/**
 * Full-screen viewer. Its own dialog rather than an app Overlay: it opens on
 * top of the giveaway pop-up, and Escape here must close only this.
 */
function GalleryViewer({ items, index, title, onIndex, onClose }: GalleryViewerProps) {
  const dialog = useRef<HTMLDivElement>(null)
  const item = items[index]
  const count = items.length

  const go = useCallback(
    (step: number) => onIndex((index + step + count) % count),
    [index, count, onIndex],
  )

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    dialog.current?.focus({ preventScroll: true })

    // Capture phase, so Escape and arrows reach this before the pop-up below.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      else if (event.key === 'ArrowRight' && count > 1) go(1)
      else if (event.key === 'ArrowLeft' && count > 1) go(-1)
      else return
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = overflow
      previous?.focus?.({ preventScroll: true })
    }
  }, [go, onClose, count])

  return createPortal(
    <div
      className="gallery-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} gallery, ${index + 1} of ${count}`}
      ref={dialog}
      tabIndex={-1}
      onClick={onClose}
    >
      <div className="gallery-viewer-stage" onClick={(event) => event.stopPropagation()}>
        {item.kind === 'image' ? (
          <img src={item.full} alt={`${title}, image ${index + 1}`} />
        ) : (
          <VideoEmbed
            key={item.key}
            url={item.video.url}
            title={`${title}, video ${index + 1}`}
            autoPlay
          />
        )}
      </div>

      <button
        type="button"
        className="gallery-viewer-close"
        onClick={onClose}
        aria-label="Close gallery"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>

      {count > 1 ? (
        <>
          <button
            type="button"
            className="gallery-viewer-nav prev"
            onClick={(event) => {
              event.stopPropagation()
              go(-1)
            }}
            aria-label="Previous"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M10 3.5 5.5 8l4.5 4.5" />
            </svg>
          </button>
          <button
            type="button"
            className="gallery-viewer-nav next"
            onClick={(event) => {
              event.stopPropagation()
              go(1)
            }}
            aria-label="Next"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M6 3.5 10.5 8 6 12.5" />
            </svg>
          </button>
          <div className="gallery-viewer-count" aria-hidden="true">
            {index + 1} / {count}
          </div>
        </>
      ) : null}
    </div>,
    document.body,
  )
}
